import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Search, Pencil, Trash2, UserPlus, Clock, CheckCircle, PauseCircle, ArrowUpDown, ArrowUp, ArrowDown, Download, DollarSign, CalendarCheck, Undo2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  in_corso: { label: 'In Corso', color: 'bg-blue-200 text-blue-800' },
  sospeso: { label: 'Sospeso', color: 'bg-yellow-200 text-yellow-800' },
  completato: { label: 'Completato', color: 'bg-green-200 text-green-800' },
};

const pagStatoConfig = {
  da_pagare: { label: 'Da Pagare', color: 'bg-amber-100 text-amber-700' },
  pagato: { label: 'Pagato', color: 'bg-green-100 text-green-700' },
  in_attesa: { label: 'In Attesa', color: 'bg-slate-100 text-slate-500' },
};

const metodiPagamento = [
  { value: 'contanti', label: 'Contanti' },
  { value: 'bonifico', label: 'Bonifico' },
  { value: 'pos', label: 'POS' },
  { value: 'assegno', label: 'Assegno' },
];

function AgentAutocomplete({ value, onChange, agenti, testId }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const filtered = agenti.filter(a => a.toLowerCase().includes((filter || value || '').toLowerCase()));
  return (
    <div className="relative" ref={ref}>
      <Input value={value} onChange={(e) => { onChange(e.target.value); setFilter(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} placeholder="Nome sub-agente..." data-testid={testId} />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {filtered.map(a => (<div key={a} className="px-3 py-2 text-sm cursor-pointer hover:bg-slate-100" onClick={() => { onChange(a); setOpen(false); }}>{a}</div>))}
        </div>
      )}
    </div>
  );
}

function ClientSearch({ clients, value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selectedClient = clients.find(c => c.id === value);
  const displayName = selectedClient ? (selectedClient.is_company && selectedClient.company_name ? selectedClient.company_name : `${selectedClient.last_name || ''} ${selectedClient.first_name || ''}`.trim()) : '';
  const filtered = clients.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return `${c.last_name || ''} ${c.first_name || ''} ${c.company_name || ''}`.toLowerCase().includes(s) || (c.fiscal_code || '').toLowerCase().includes(s);
  });
  return (
    <div className="relative" ref={ref}>
      <Input value={open ? search : displayName} onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }} onFocus={() => { setOpen(true); setSearch(''); }} placeholder="Cerca cliente..." data-testid={testId} />
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {filtered.length === 0 ? <div className="px-3 py-2 text-sm text-slate-400">Nessun cliente trovato</div> : filtered.map(c => {
            const name = c.is_company && c.company_name ? c.company_name : `${c.last_name || ''} ${c.first_name || ''}`.trim();
            return (<div key={c.id} className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 ${c.id === value ? 'bg-slate-50 font-medium' : ''}`} onClick={() => { onChange(c.id); setSearch(''); setOpen(false); }}>{name} {c.fiscal_code ? <span className="text-slate-400 text-xs ml-2">{c.fiscal_code}</span> : null}</div>);
          })}
        </div>
      )}
    </div>
  );
}

const emptyForm = { agente: '', client_id: '', fornitore: '', stato: 'in_corso', provvigione: 0, data_inizio: '', fornitura_attiva: true, data_cessazione: '', notes: '' };
const fmt = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);

export default function SubAgenti() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [agenti, setAgenti] = useState([]);
  const [pagamenti, setPagamenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStato, setFilterStato] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [formData, setFormData] = useState(emptyForm);
  const [activeTab, setActiveTab] = useState('segnalazioni');
  const [pagSearchTerm, setPagSearchTerm] = useState('');
  const [pagFilterStato, setPagFilterStato] = useState('all');
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payingItem, setPayingItem] = useState(null);
  const [payMethod, setPayMethod] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [itemsRes, clientsRes, agentiRes] = await Promise.all([
        axios.get(`${API}/api/sub-agenti`),
        axios.get(`${API}/api/clients`),
        axios.get(`${API}/api/sub-agenti/agenti-list`)
      ]);
      setItems(itemsRes.data);
      setClients(clientsRes.data);
      setAgenti(agentiRes.data);
    } catch (e) { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  }, []);

  const fetchPagamenti = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/sub-agenti/pagamenti`);
      setPagamenti(res.data);
    } catch (e) {}
  }, []);

  useEffect(() => { fetchData(); fetchPagamenti(); }, [fetchData, fetchPagamenti]);

  const openNew = () => { setEditing(null); setFormData(emptyForm); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    setFormData({ agente: item.agente || '', client_id: item.client_id || '', fornitore: item.fornitore || '', stato: item.stato || 'in_corso', provvigione: item.provvigione || 0, data_inizio: item.data_inizio || '', fornitura_attiva: item.fornitura_attiva !== false, data_cessazione: item.data_cessazione || '', notes: item.notes || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.agente) { toast.error('Inserire il nome del sub-agente'); return; }
    if (!formData.client_id) { toast.error('Selezionare un cliente'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/api/sub-agenti/${editing.id}`, formData);
        toast.success('Segnalazione aggiornata');
      } else {
        await axios.post(`${API}/api/sub-agenti`, formData);
        toast.success('Segnalazione creata');
      }
      setDialogOpen(false);
      fetchData();
      fetchPagamenti();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa segnalazione?')) return;
    try {
      await axios.delete(`${API}/api/sub-agenti/${id}`);
      toast.success('Eliminata');
      fetchData(); fetchPagamenti();
    } catch (e) { toast.error('Errore'); }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await axios.put(`${API}/api/sub-agenti/${item.id}`, { stato: newStatus });
      toast.success(`Stato: ${statusConfig[newStatus]?.label}`);
      fetchData();
    } catch (e) { toast.error('Errore'); }
  };

  const openPayDialog = (pag) => { setPayingItem(pag); setPayMethod(''); setPayDialogOpen(true); };

  const handlePay = async () => {
    if (!payingItem) return;
    try {
      await axios.post(`${API}/api/sub-agenti/pagamenti/${payingItem.segnalazione_id}/${payingItem.semestre}/paga`, { metodo_pagamento: payMethod, importo: payingItem.importo });
      toast.success('Pagamento registrato');
      setPayDialogOpen(false);
      fetchPagamenti();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleUndoPay = async (pagamentoId) => {
    if (!window.confirm('Annullare questo pagamento?')) return;
    try {
      await axios.delete(`${API}/api/sub-agenti/pagamenti/${pagamentoId}`);
      toast.success('Pagamento annullato');
      fetchPagamenti();
    } catch (e) { toast.error('Errore'); }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-300" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-slate-700" /> : <ArrowDown className="w-3 h-3 ml-1 text-slate-700" />;
  };

  const filtered = items
    .filter(item => {
      if (filterStato !== 'all' && item.stato !== filterStato) return false;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        return (item.agente || '').toLowerCase().includes(s) || (item.client_name || '').toLowerCase().includes(s) || (item.fornitore || '').toLowerCase().includes(s);
      }
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'created_at') cmp = new Date(a.created_at || 0) - new Date(b.created_at || 0);
      else if (sortField === 'provvigione') cmp = (a.provvigione || 0) - (b.provvigione || 0);
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const filteredPag = pagamenti.filter(p => {
    if (pagFilterStato !== 'all' && p.stato !== pagFilterStato) return false;
    if (pagSearchTerm) {
      const s = pagSearchTerm.toLowerCase();
      return (p.agente || '').toLowerCase().includes(s) || (p.client_name || '').toLowerCase().includes(s) || (p.fornitore || '').toLowerCase().includes(s);
    }
    return true;
  });

  const totaleDaPagare = pagamenti.filter(p => p.stato === 'da_pagare').reduce((s, p) => s + (p.importo || 0), 0);
  const totalePagato = pagamenti.filter(p => p.stato === 'pagato').reduce((s, p) => s + (p.importo || 0), 0);
  const totaleInAttesa = pagamenti.filter(p => p.stato === 'in_attesa').reduce((s, p) => s + (p.importo || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Sub-Agenti</h1>
          <p className="text-slate-600 mt-1">Gestione segnalazioni e provvigioni ricorrenti</p>
        </div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="new-sub-agente-btn">
          <Plus className="w-4 h-4 mr-2" />Nuova Segnalazione
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="segnalazioni" className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Segnalazioni ({items.length})
          </TabsTrigger>
          <TabsTrigger value="pagamenti" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> Pagamenti ({pagamenti.length})
          </TabsTrigger>
        </TabsList>

        {/* ===== TAB SEGNALAZIONI ===== */}
        <TabsContent value="segnalazioni">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[250px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Cerca per agente, cliente, fornitore..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-sub-agenti" />
              </div>
              <Select value={filterStato} onValueChange={setFilterStato}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Stato" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="in_corso">In Corso</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                  <SelectItem value="completato">Completato</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border p-4">
                <div className="text-2xl font-bold text-slate-900">{items.length}</div>
                <div className="text-sm text-slate-500">Segnalazioni Totali</div>
              </div>
              <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                <div className="text-2xl font-bold text-blue-700">{items.filter(i => i.stato === 'in_corso').length}</div>
                <div className="text-sm text-blue-600">In Corso</div>
              </div>
              <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                <div className="text-2xl font-bold text-green-700">{items.filter(i => i.stato === 'completato').length}</div>
                <div className="text-sm text-green-600">Completate</div>
              </div>
            </div>

            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Agente</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Cliente</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Fornitore</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Data Inizio</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase cursor-pointer select-none" onClick={() => toggleSort('created_at')}>
                      <span className="flex items-center">Inserimento <SortIcon field="created_at" /></span>
                    </th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Stato</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase cursor-pointer select-none" onClick={() => toggleSort('provvigione')}>
                      <span className="flex items-center">Provv./Anno <SortIcon field="provvigione" /></span>
                    </th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Fornitura</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Cessazione</th>
                    <th className="text-right p-3 text-xs font-medium text-slate-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="10" className="text-center py-8 text-slate-500">Caricamento...</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan="10" className="text-center py-8 text-slate-500">Nessuna segnalazione trovata</td></tr>
                  ) : filtered.map(item => {
                    const status = statusConfig[item.stato] || statusConfig.in_corso;
                    return (
                      <tr key={item.id} className="border-b hover:bg-slate-50 transition-colors" data-testid="sub-agente-row">
                        <td className="p-3"><div className="flex items-center gap-2"><UserPlus className="w-4 h-4 text-slate-400 flex-shrink-0" /><span className="text-sm font-medium text-slate-900">{item.agente || '-'}</span></div></td>
                        <td className="p-3"><span className="text-sm text-slate-700">{item.client_name || '-'}</span></td>
                        <td className="p-3"><span className="text-sm text-slate-700">{item.fornitore || '-'}</span></td>
                        <td className="p-3"><span className="text-sm text-slate-600">{item.data_inizio ? new Date(item.data_inizio).toLocaleDateString('it-IT') : '-'}</span></td>
                        <td className="p-3"><span className="text-sm text-slate-500">{item.created_at ? new Date(item.created_at).toLocaleDateString('it-IT') : '-'}</span></td>
                        <td className="p-3">
                          <Select value={item.stato} onValueChange={(v) => handleStatusChange(item, v)}>
                            <SelectTrigger className="h-7 w-[130px] border-0 p-0 bg-transparent"><Badge className={`cursor-pointer ${status.color}`}>{status.label}</Badge></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="in_corso">In Corso</SelectItem>
                              <SelectItem value="sospeso">Sospeso</SelectItem>
                              <SelectItem value="completato">Completato</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3">{item.provvigione > 0 ? <span className="text-sm font-medium text-slate-900">{fmt(item.provvigione)}</span> : <span className="text-sm text-slate-400">-</span>}</td>
                        <td className="p-3">
                          <Badge className={`cursor-pointer ${item.fornitura_attiva !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                            onClick={async () => {
                              const newActive = item.fornitura_attiva === false;
                              const update = { fornitura_attiva: newActive };
                              if (!newActive) update.data_cessazione = new Date().toISOString().split('T')[0];
                              else update.data_cessazione = '';
                              try {
                                await axios.put(`${API}/api/sub-agenti/${item.id}`, update);
                                toast.success(newActive ? 'Fornitura riattivata' : 'Fornitura cessata');
                                fetchData(); fetchPagamenti();
                              } catch(e) { toast.error('Errore'); }
                            }}
                          >
                            {item.fornitura_attiva !== false ? 'Attiva' : 'Cessata'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {item.fornitura_attiva === false && item.data_cessazione ? (
                            <span className="text-sm text-red-600 font-medium">{new Date(item.data_cessazione).toLocaleDateString('it-IT')}</span>
                          ) : <span className="text-sm text-slate-400">-</span>}
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(item)} data-testid="edit-sub-agente-btn"><Pencil className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(item.id)} data-testid="delete-sub-agente-btn"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ===== TAB PAGAMENTI ===== */}
        <TabsContent value="pagamenti">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[250px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Cerca per agente, cliente, fornitore..." value={pagSearchTerm} onChange={(e) => setPagSearchTerm(e.target.value)} className="pl-10" data-testid="search-pagamenti" />
              </div>
              <Select value={pagFilterStato} onValueChange={setPagFilterStato}>
                <SelectTrigger className="w-[180px]"><SelectValue placeholder="Stato" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="da_pagare">Da Pagare</SelectItem>
                  <SelectItem value="pagato">Pagato</SelectItem>
                  <SelectItem value="in_attesa">In Attesa</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                <div className="text-2xl font-bold text-amber-700">{fmt(totaleDaPagare)}</div>
                <div className="text-sm text-amber-600">Da Pagare</div>
              </div>
              <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                <div className="text-2xl font-bold text-green-700">{fmt(totalePagato)}</div>
                <div className="text-sm text-green-600">Pagato</div>
              </div>
              <div className="bg-slate-50 rounded-xl border p-4">
                <div className="text-2xl font-bold text-slate-500">{fmt(totaleInAttesa)}</div>
                <div className="text-sm text-slate-400">In Attesa (Futuro)</div>
              </div>
            </div>

            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Agente</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Cliente</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Fornitore</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Sem.</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Periodo</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Importo</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Stato</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Pagamento</th>
                    <th className="text-right p-3 text-xs font-medium text-slate-500 uppercase">Azione</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPag.length === 0 ? (
                    <tr><td colSpan="9" className="text-center py-8 text-slate-500">
                      {pagamenti.length === 0 ? 'Nessuna segnalazione con data inizio e provvigione configurata' : 'Nessun pagamento trovato'}
                    </td></tr>
                  ) : (() => {
                    // Group by agente
                    const grouped = {};
                    filteredPag.forEach(pag => {
                      const key = pag.agente || '-';
                      if (!grouped[key]) grouped[key] = [];
                      grouped[key].push(pag);
                    });
                    return Object.entries(grouped).map(([agente, rows]) => {
                      const totDaPagare = rows.filter(r => r.stato === 'da_pagare').reduce((s, r) => s + (r.importo || 0), 0);
                      const totPagato = rows.filter(r => r.stato === 'pagato').reduce((s, r) => s + (r.importo || 0), 0);
                      const totInAttesa = rows.filter(r => r.stato === 'in_attesa').reduce((s, r) => s + (r.importo || 0), 0);
                      const totale = rows.reduce((s, r) => s + (r.importo || 0), 0);
                      return (
                        <React.Fragment key={agente}>
                          <tr className="bg-slate-100 border-b-2 border-slate-300">
                            <td colSpan="5" className="p-3">
                              <div className="flex items-center gap-2">
                                <UserPlus className="w-4 h-4 text-slate-600" />
                                <span className="text-sm font-bold text-slate-800">{agente}</span>
                                <span className="text-xs text-slate-500 ml-2">({rows.length} {rows.length === 1 ? 'pagamento' : 'pagamenti'})</span>
                              </div>
                            </td>
                            <td className="p-3"><span className="text-sm font-bold text-slate-800">{fmt(totale)}</span></td>
                            <td colSpan="2" className="p-3">
                              <div className="flex gap-2 flex-wrap">
                                {totDaPagare > 0 && <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">Da Pagare: {fmt(totDaPagare)}</span>}
                                {totPagato > 0 && <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded">Pagato: {fmt(totPagato)}</span>}
                                {totInAttesa > 0 && <span className="text-xs font-medium text-slate-500 bg-slate-200 px-2 py-0.5 rounded">In Attesa: {fmt(totInAttesa)}</span>}
                              </div>
                            </td>
                            <td></td>
                          </tr>
                          {rows.map(pag => {
                            const ps = pagStatoConfig[pag.stato] || pagStatoConfig.da_pagare;
                            const metodoLabel = metodiPagamento.find(m => m.value === pag.metodo_pagamento)?.label || pag.metodo_pagamento || '';
                            return (
                              <tr key={`${pag.segnalazione_id}_${pag.semestre}`} className={`border-b transition-colors ${pag.stato === 'pagato' ? 'bg-green-50/50' : pag.stato === 'in_attesa' ? 'bg-slate-50/50' : 'hover:bg-amber-50/30'}`} data-testid="pagamento-row">
                                <td className="p-3 pl-8"><span className="text-sm text-slate-400">{pag.agente}</span></td>
                                <td className="p-3"><span className="text-sm text-slate-700">{pag.client_name}</span></td>
                                <td className="p-3"><span className="text-sm text-slate-700">{pag.fornitore || '-'}</span></td>
                                <td className="p-3"><Badge variant="outline" className="text-xs">{pag.semestre}°</Badge></td>
                                <td className="p-3"><span className="text-xs text-slate-500">{pag.periodo_da} - {pag.periodo_a}</span></td>
                                <td className="p-3"><span className="text-sm font-medium text-slate-900">{fmt(pag.importo)}{pag.mesi_label && <span className="text-xs text-amber-600 ml-1">({pag.mesi_label})</span>}</span></td>
                                <td className="p-3"><Badge className={ps.color}>{ps.label}</Badge></td>
                                <td className="p-3">
                                  {pag.stato === 'pagato' ? (
                                    <div className="text-xs text-slate-600">
                                      <div>{pag.data_pagamento ? new Date(pag.data_pagamento).toLocaleDateString('it-IT') : '-'}</div>
                                      {metodoLabel && <div className="text-slate-400">{metodoLabel}</div>}
                                    </div>
                                  ) : <span className="text-sm text-slate-400">-</span>}
                                </td>
                                <td className="p-3 text-right">
                                  {pag.stato === 'da_pagare' && (
                                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs" onClick={() => openPayDialog(pag)} data-testid="paga-btn">
                                      <CalendarCheck className="w-3 h-3 mr-1" />Paga
                                    </Button>
                                  )}
                                  {pag.stato === 'pagato' && pag.pagamento_id && (
                                    <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-500 h-7" onClick={() => handleUndoPay(pag.pagamento_id)} title="Annulla pagamento" data-testid="annulla-paga-btn">
                                      <Undo2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                  {pag.stato === 'in_attesa' && <span className="text-xs text-slate-400">Non maturato</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog Crea/Modifica Segnalazione */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Modifica Segnalazione' : 'Nuova Segnalazione Sub-Agente'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Sub-Agente *</Label>
              <AgentAutocomplete value={formData.agente} onChange={(v) => setFormData({ ...formData, agente: v })} agenti={agenti} testId="sub-agente-name" />
            </div>
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <ClientSearch clients={clients} value={formData.client_id} onChange={(id) => setFormData({ ...formData, client_id: id })} testId="sub-agente-client" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fornitore</Label>
                <Input value={formData.fornitore} onChange={(e) => setFormData({ ...formData, fornitore: e.target.value })} placeholder="Nome fornitore..." data-testid="sub-agente-fornitore" />
              </div>
              <div className="space-y-2">
                <Label>Stato</Label>
                <Select value={formData.stato} onValueChange={(v) => setFormData({ ...formData, stato: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_corso">In Corso</SelectItem>
                    <SelectItem value="sospeso">Sospeso</SelectItem>
                    <SelectItem value="completato">Completato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="font-medium">Fornitura Attiva</Label>
                <p className="text-xs text-slate-400">Se cessata, le provvigioni future non vengono generate</p>
              </div>
              <Badge className={`cursor-pointer text-sm px-3 py-1 ${formData.fornitura_attiva ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                onClick={() => setFormData({ ...formData, fornitura_attiva: !formData.fornitura_attiva, data_cessazione: !formData.fornitura_attiva ? '' : formData.data_cessazione })}
                data-testid="sub-agente-fornitura"
              >
                {formData.fornitura_attiva ? 'Attiva' : 'Cessata'}
              </Badge>
            </div>
            {!formData.fornitura_attiva && (
              <div className="space-y-2">
                <Label>Data Cessazione Fornitura</Label>
                <Input type="date" value={formData.data_cessazione} onChange={(e) => setFormData({ ...formData, data_cessazione: e.target.value })} data-testid="sub-agente-data-cessazione" />
                <p className="text-xs text-slate-400">L'ultimo semestre verrà calcolato pro-rata in base ai mesi effettivi</p>
              </div>
            )}
            <div className="border-t pt-4 mt-2"><Label className="text-base font-medium">Provvigione</Label></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importo Annuo (€)</Label>
                <Input type="number" step="0.01" value={formData.provvigione} onChange={(e) => setFormData({ ...formData, provvigione: parseFloat(e.target.value) || 0 })} placeholder="0.00" data-testid="sub-agente-provvigione" />
                <p className="text-xs text-slate-400">Pagamento semestrale: {fmt((formData.provvigione || 0) / 2)}</p>
              </div>
              <div className="space-y-2">
                <Label>Data Inizio Contratto</Label>
                <Input type="date" value={formData.data_inizio} onChange={(e) => setFormData({ ...formData, data_inizio: e.target.value })} data-testid="sub-agente-data-inizio" />
              </div>
            </div>
            <p className="text-xs text-slate-400">La provvigione viene divisa in 2 pagamenti semestrali dalla data inizio. Vai nella tab "Pagamenti" per gestire.</p>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} placeholder="Note aggiuntive..." />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button onClick={handleSave} className="bg-slate-900 hover:bg-slate-800" data-testid="save-sub-agente-btn">{editing ? 'Salva' : 'Crea Segnalazione'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Segna Come Pagato */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Segna Come Pagato</DialogTitle></DialogHeader>
          {payingItem && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                <div><span className="text-slate-500">Agente:</span> <span className="font-medium">{payingItem.agente}</span></div>
                <div><span className="text-slate-500">Cliente:</span> <span className="font-medium">{payingItem.client_name}</span></div>
                <div><span className="text-slate-500">Semestre:</span> <span className="font-medium">{payingItem.semestre}° ({payingItem.periodo_da} - {payingItem.periodo_a})</span></div>
                <div><span className="text-slate-500">Importo:</span> <span className="font-bold text-green-700">{fmt(payingItem.importo)}</span></div>
              </div>
              <div className="space-y-2">
                <Label>Metodo Pagamento</Label>
                <Select value={payMethod || 'none'} onValueChange={(v) => setPayMethod(v === 'none' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-</SelectItem>
                    {metodiPagamento.map(m => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setPayDialogOpen(false)}>Annulla</Button>
                <Button className="bg-green-600 hover:bg-green-700" onClick={handlePay} data-testid="confirm-pay-btn">
                  <CheckCircle className="w-4 h-4 mr-2" />Conferma Pagamento
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
